import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faMobile, faCheckCircle, faTimes, faSpinner } from "@fortawesome/free-solid-svg-icons";

const DarbarVerifyOTP = ({ isOpen, onClose, userData }) => {
  const navigate = useNavigate();
  const [emailOTP, setEmailOTP] = useState("");
  const [mobileOTP, setMobileOTP] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(180); // 3 minutes in seconds
  const [canResendOTP, setCanResendOTP] = useState(false);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Set up countdown timer
  useEffect(() => {
    if (!isOpen) return;

    // Create a demo OTP for testing purposes
    const demoOTP = Math.floor(100000 + Math.random() * 900000).toString();
    sessionStorage.setItem('darbarResetDemoOTP', demoOTP);

    // Show the OTP in an alert for demo purposes
    alert(`For demo purposes, your OTP is: ${demoOTP}\n\nIn a real application, this would be sent via SMS and email.`);

    // Set success message with the OTP
    setSuccess(`Use "${demoOTP}" for both OTP fields.`);

    // Start the timer
    const countdown = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(countdown);
          setCanResendOTP(true);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);

    // Clear timer on unmount
    return () => clearInterval(countdown);
  }, [isOpen]);

  // Handle OTP resend
  const handleResendOTP = () => {
    if (!canResendOTP || isResending) return;

    setIsResending(true);
    setError("");

    // Simulate API delay for demo
    setTimeout(() => {
      // Create a new demo OTP
      const newDemoOTP = Math.floor(100000 + Math.random() * 900000).toString();
      sessionStorage.setItem('darbarResetDemoOTP', newDemoOTP);

      // Reset timer and state
      setTimer(180);
      setCanResendOTP(false);

      // Show success message with the new OTP
      alert(`OTP resent successfully!\n\nFor demo purposes, your new OTP is: ${newDemoOTP}\n\nIn a real application, this would be sent via SMS and email.`);

      setSuccess(`OTP resent successfully! Use "${newDemoOTP}" for both fields.`);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess("");
      }, 5000);

      setIsResending(false);
    }, 1500); // Simulate 1.5 second delay
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    setSuccess("");

    // Validate OTPs
    if (!emailOTP || !mobileOTP) {
      setError("Please enter both OTPs");
      setIsSubmitting(false);
      return;
    }

    // Get the demo OTP from session storage if available
    const savedOTP = sessionStorage.getItem('darbarResetDemoOTP') || "123456";

    // Simulate API delay
    setTimeout(() => {
      // Demo validation - accept the saved OTP or fallback to "123456"
      if (emailOTP === savedOTP && mobileOTP === savedOTP) {
        setSuccess("Verification successful!");

        // Redirect to login page after 2 seconds
        setTimeout(() => {
          // For demo, show an alert before redirecting
          alert("Verification successful! Redirecting to login page...");
          onClose();
          navigate("/darbar-login");
        }, 2000);
      } else {
        // For demo, show a hint about the correct OTP
        setError(`OTP verification failed. Hint: Use "${savedOTP}" for both OTPs`);
      }

      setIsSubmitting(false);
    }, 1500); // Simulate 1.5 second delay
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-auto p-6 relative"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <FontAwesomeIcon icon={faTimes} className="text-lg" />
        </button>

        <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">
          Verify Your Identity
        </h2>
        <p className="text-center text-gray-600 text-sm mb-4">
          <span className="bg-yellow-100 px-2 py-1 rounded text-yellow-800">
            Demo Mode: Use the OTP shown in the alert for both fields
          </span>
        </p>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm font-medium mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm font-medium mb-4">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-blue-500" />
              Email OTP
            </label>
            <input
              type="text"
              value={emailOTP}
              onChange={(e) => setEmailOTP(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter 6-digit OTP sent to your email"
              maxLength={6}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FontAwesomeIcon icon={faMobile} className="mr-2 text-blue-500" />
              Mobile OTP
            </label>
            <input
              type="text"
              value={mobileOTP}
              onChange={(e) => setMobileOTP(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter 6-digit OTP sent to your mobile"
              maxLength={6}
            />
          </div>

          <div className="flex justify-between items-center text-sm">
            <div className="text-gray-600">
              Time remaining: <span className="font-medium text-blue-600">{formatTime(timer)}</span>
            </div>
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={!canResendOTP || isResending}
              className={`text-blue-600 hover:text-blue-800 ${
                !canResendOTP || isResending ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isResending ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="fa-spin mr-1" />
                  Resending...
                </>
              ) : (
                "Resend OTP"
              )}
            </button>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {isSubmitting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin mr-2" />
                Verifying...
              </>
            ) : (
              "Verify OTP"
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default DarbarVerifyOTP;
